import React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  Alert,
  AlertTitle,
  Button,
  Container,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import BuildIcon from "@mui/icons-material/Build";
import PaletteIcon from "@mui/icons-material/Palette";
import CodeIcon from "@mui/icons-material/Code";
import LaunchIcon from "@mui/icons-material/Launch";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { useWizard } from "../WizardContext";

/**
 * Wizard Introduction - Clean, focused introduction page
 * Provides clear overview and easy entry point into the wizard
 */
const WizardIntroduction = () => {
  const { setCurrentStep } = useWizard();

  const features = [
    {
      icon: BuildIcon,
      title: "Choose Your Blocks",
      desc: "Select from 14 professionally designed content blocks",
    },
    {
      icon: PaletteIcon,
      title: "Customize Your Style",
      desc: "Match your website's colors, fonts, and branding",
    },
    {
      icon: CodeIcon,
      title: "Get Your Code",
      desc: "Copy ready-to-use embed code for your website",
    },
    {
      icon: LaunchIcon,
      title: "Go Live",
      desc: "Paste into WordPress, Webflow, or any website",
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      {/* Hero Section */}
      <Box
        sx={{
          textAlign: "center",
          mb: { xs: 3, sm: 4, md: 6 },
          py: { xs: 3, sm: 4, md: 6 },
          px: { xs: 2, sm: 3, md: 4 },
          background: "linear-gradient(135deg, #1C436A 0%, #16365A 100%)",
          borderRadius: { xs: 2, md: 3 },
          color: "white",
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: "bold",
            mb: { xs: 2, md: 4 },
            mt: 2,
            fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3rem" },
          }}
        >
          TPCI's Partnership Charter Webpage Builder
        </Typography>
        <Typography
          variant="h6"
          sx={{
            mb: { xs: 3, md: 4 },
            opacity: 0.9,
            maxWidth: "600px",
            mx: "auto",
            fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
            px: { xs: 2, sm: 0 },
          }}
        >
          Create professional Partnership Charter content for your website in
          minutes. No coding required.
        </Typography>

        {/* See How It Works Link */}
        <Box
          onClick={() => {
            document
              .getElementById("how-it-works")
              .scrollIntoView({ behavior: "smooth" });
          }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            cursor: "pointer",
            color: "white",
            opacity: 0.9,
            transition: "opacity 0.2s",
            "&:hover": {
              opacity: 1,
            },
          }}
        >
          <Typography variant="body1" sx={{ mr: 1 }}>
            See How It Works
          </Typography>
          <KeyboardArrowDownIcon />
        </Box>
      </Box>

      {/* How It Works - Simplified */}
      <Box id="how-it-works" sx={{ mb: { xs: 3, sm: 4, md: 6 } }}>
        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          sx={{
            textAlign: "center",
            mb: { xs: 3, md: 4 },
            color: "#1e4164",
            fontWeight: "bold",
            fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
          }}
        >
          How It Works
        </Typography>

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  elevation={2}
                  sx={{
                    height: "100%",
                    textAlign: "center",
                    p: 3,
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
                    },
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        bgcolor: "#1C436A",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                      }}
                    >
                      <IconComponent sx={{ color: "white", fontSize: 28 }} />
                    </Box>
                    <Typography
                      sx={{
                        position: "absolute",
                        top: -10,
                        right: "50%",
                        transform: "translateX(50%)",
                        bgcolor: "#CE950F",
                        color: "white",
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                      }}
                    >
                      {index + 1}
                    </Typography>
                  </Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: "bold", color: "#1e4164" }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.desc}
                  </Typography>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* CTA Section - After How It Works */}
      <Box
        sx={{
          textAlign: "center",
          py: { xs: 4, sm: 6, md: 8 },
          px: { xs: 2, sm: 3, md: 4 },
          mb: { xs: 4, sm: 6, md: 8 },
          bgcolor: "#f8f9fc",
          borderRadius: { xs: 2, md: 3 },
        }}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: "#1e4164",
            fontWeight: "bold",
            mb: { xs: 2, md: 3 },
            fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.5rem" },
            px: { xs: 2, sm: 0 },
          }}
        >
          Ready to create your website's Partnership Charter content?
        </Typography>
        {/* <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 4,
            maxWidth: "500px",
            mx: "auto",
          }}
        >
          Add professionally designed PC content
          <br />
          to your Firm's website.
        </Typography> */}

        {/* Main CTA Button */}
        <Button
          variant="contained"
          size="large"
          onClick={() => setCurrentStep(1)}
          sx={{
            bgcolor: "#CE950F",
            "&:hover": { bgcolor: "#B5850D" },
            px: { xs: 4, sm: 6 },
            py: { xs: 1.5, md: 2 },
            fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
            fontWeight: "bold",
            borderRadius: { xs: 2, md: 3 },
            textTransform: "none",
            boxShadow: "0 8px 20px rgba(206, 149, 15, 0.3)",
          }}
        >
          Start Building Now
        </Button>
      </Box>

    </Container>
  );
};

export default WizardIntroduction;
